import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { trackPastProjectClick } from './userJourneyTracker';
import './PastProjects.css';

function formatProjectDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ProjectThumbnail({ thumbnailUrl, getFreshTokenRef, alt }) {
  const [src, setSrc] = useState(null);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const token = await getFreshTokenRef.current();
        if (!token || cancelled) return;
        const res = await fetch(thumbnailUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setSrc(url);
      } catch {
        /* ignore */
      }
    }

    load();
    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [thumbnailUrl, getFreshTokenRef]);

  if (!src) {
    return (
      <div className="past-projects__thumb past-projects__thumb--placeholder" aria-hidden="true">
        <span className="past-projects__thumb-icon">▶</span>
      </div>
    );
  }

  return <img src={src} alt={alt} className="past-projects__thumb" loading="lazy" />;
}

export default function PastProjects() {
  const { user, getFreshToken } = useAuth();
  const getFreshTokenRef = useRef(getFreshToken);
  getFreshTokenRef.current = getFreshToken;

  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userId = user?.uid;
    if (!userId) {
      setProjects([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function loadProjects() {
      try {
        const token = await getFreshTokenRef.current();
        if (!token || cancelled) {
          if (!cancelled) setProjects([]);
          return;
        }
        const res = await fetch('/api/user/projects?limit=3', {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (cancelled) return;
        if (!res.ok) {
          setProjects([]);
          return;
        }
        const data = await res.json();
        setProjects(data.projects || []);
      } catch {
        if (!cancelled) setProjects([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    setLoading(true);
    loadProjects();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  if (loading) {
    return (
      <section className="past-projects" aria-label="Past projects">
        <h3 className="past-projects__title">Past Projects</h3>
        <p className="past-projects__status">Loading your projects…</p>
      </section>
    );
  }

  if (!projects.length) {
    return null;
  }

  return (
    <section className="past-projects" aria-label="Past projects">
      <h3 className="past-projects__title">Past Projects</h3>
      <div className="past-projects__scroll">
        {projects.map((project) => {
          const displayName = (project.file_name || project.file_stem).replace(/\.[^.]+$/, '');
          const clipCount = project.clip_count || 0;

          return (
            <button
              key={project.file_stem}
              type="button"
              className="past-projects__card"
              onClick={() => {
                trackPastProjectClick(project.file_stem);
                navigate(`/projects/${encodeURIComponent(project.file_stem)}`);
              }}
              aria-label={`Open project ${displayName}, ${clipCount} clip${clipCount === 1 ? '' : 's'}`}
            >
              <div className="past-projects__thumb-wrap">
                <ProjectThumbnail
                  thumbnailUrl={project.thumbnail_url}
                  getFreshTokenRef={getFreshTokenRef}
                  alt=""
                />
                <span className="past-projects__shade" aria-hidden="true" />
                <span
                  className="past-projects__clip-count"
                  aria-hidden="true"
                >
                  <span className="past-projects__clip-count-num">{clipCount}</span>
                  <span className="past-projects__clip-count-label">
                    {clipCount === 1 ? 'clip' : 'clips'}
                  </span>
                </span>
              </div>
              <div className="past-projects__meta">
                <span className="past-projects__name" title={displayName}>
                  {displayName}
                </span>
                {project.uploaded_at && (
                  <span className="past-projects__date">{formatProjectDate(project.uploaded_at)}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
